import React, { useState, useCallback, useMemo } from 'react';
import Cropper from 'react-easy-crop';
import { FiCheck, FiX } from 'react-icons/fi';
import { useI18n } from '../utilities/i18n';
import '../styles/createMatchModal.css';
import '../styles/imageCropper.css';

export default function ImageCropper({
  image,
  onCropComplete,
  onCancel,
  aspect = 1,
  title,
  outputWidth,
}) {
  const { t } = useI18n();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const safeAspect = Number(aspect) > 0 ? Number(aspect) : 1;
  const isPortrait = safeAspect < 0.95;

  const onCropCompleteCallback = useCallback((_croppedArea, nextCroppedAreaPixels) => {
    setCroppedAreaPixels(nextCroppedAreaPixels);
  }, []);

  const createCroppedImage = async () => {
    if (!croppedAreaPixels || busy) return;
    setBusy(true);
    try {
      const croppedImage = await getCroppedImg(image, croppedAreaPixels, {
        outputWidth: outputWidth > 0 ? Number(outputWidth) : null,
      });
      onCropComplete(croppedImage);
    } catch (e) {
      console.error('Error cropping image:', e);
    } finally {
      setBusy(false);
    }
  };

  const modalStyle = useMemo(() => (
    isPortrait ? { width: 'min(480px, 100%)' } : undefined
  ), [isPortrait]);

  return (
    <div className="imageCropperOverlay">
      <div className={`imageCropperModal${isPortrait ? ' is-portrait' : ''}`} style={modalStyle}>
        <div className="imageCropperHeader">
          <h3>{title || t('epmCropTitle')}</h3>
          <button type="button" className="modal-close" onClick={onCancel} disabled={busy}>
            <FiX />
          </button>
        </div>
        <div className={`cropperContainer${isPortrait ? ' is-portrait' : ''}`}>
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            aspect={safeAspect}
            onCropChange={setCrop}
            onCropComplete={onCropCompleteCallback}
            onZoomChange={setZoom}
            showGrid
          />
        </div>
        <div className="cropperControls">
          <div className="zoomControl">
            <label>{t('epmZoom')}</label>
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e) => setZoom(Number(e.target.value))}
            />
          </div>
          <div className="cropperButtons">
            <button type="button" className="btn-modal btn-secondary-modal" onClick={onCancel} disabled={busy}>
              {t('cancel')}
            </button>
            <button
              type="button"
              className="btn-modal btn-primary-modal"
              onClick={createCroppedImage}
              disabled={busy || !croppedAreaPixels}
            >
              <FiCheck /> {busy ? t('igUploadBusy') : t('epmApply')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const getCroppedImg = (imageSrc, pixelCrop, { outputWidth = null } = {}) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const sourceW = Math.max(1, Math.round(pixelCrop.width));
      const sourceH = Math.max(1, Math.round(pixelCrop.height));
      let targetW = sourceW;
      let targetH = sourceH;

      if (outputWidth && outputWidth > 0) {
        targetW = Math.round(outputWidth);
        targetH = Math.max(1, Math.round((sourceH / sourceW) * targetW));
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = targetW;
      canvas.height = targetH;

      ctx.drawImage(
        image,
        Math.round(pixelCrop.x),
        Math.round(pixelCrop.y),
        sourceW,
        sourceH,
        0,
        0,
        targetW,
        targetH
      );

      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }, 'image/jpeg', 0.92);
    };
    image.onerror = reject;
    image.src = imageSrc;
  });
};
